/**
 * Alpha falloff shader — overlay model.
 *
 * Takes background pixels and outputs them with a soft-edged band mask:
 * opaque inside the band, transparent outside. The result is drawn ON TOP of
 * the subject so the background appears to "fog" over the subject's bottom,
 * blending its hard cut-edge naturally.
 *
 * The band spans `u_bandX` horizontally and sits between the subject's feet
 * (`u_floor`) and a parabolic waist line. The waist height is
 * `u_bandHeightRatio` of the way from the feet up to the subject's alpha top
 * (`u_ceiling`).
 *
 * Coordinate convention: every spatial uniform is in canvas UV space [0, 1]
 * with origin at the TOP-LEFT.
 */

export const FRAGMENT_SHADER = /* glsl */ `#version 300 es
precision mediump float;

in vec2 v_texCoord;
out vec4 fragColor;

uniform sampler2D u_source;       // background pixels at canvas resolution

uniform vec2  u_bandX;            // horizontal band [x1, x2] in canvas UV
uniform float u_bandXSmooth;      // half-width of the lateral smooth edges

uniform float u_ceiling;          // subject's alpha top in canvas UV.y
uniform float u_floor;            // subject's feet in canvas UV.y (bottom of the band)
uniform float u_bandHeightRatio;  // waist height as a fraction of (floor - ceiling)
uniform float u_waistSmooth;      // half-width of the smooth transition at the waist
uniform float u_waistCurvature;   // bends the waist into a parabolic arc (0 = flat line)

// The texture may cover a sub-region of the canvas. These uniforms tell the
// shader where that region sits in canvas UV so user params keep their
// canvas-relative semantics regardless of how the texture was cropped.
uniform vec2  u_bboxOrigin;       // bbox.left / canvasW, bbox.top / canvasH
uniform vec2  u_bboxSize;         // bbox.width / canvasW, bbox.height / canvasH

void main() {
  vec2 uv = v_texCoord;
  vec4 src = texture(u_source, uv);

  // Map texture UV → canvas UV so the band / waist math stays canvas-anchored.
  vec2 canvasUV = u_bboxOrigin + uv * u_bboxSize;

  // Soft-edged horizontal band [u_bandX.x, u_bandX.y].
  float leftEdge  = smoothstep(u_bandX.x - u_bandXSmooth, u_bandX.x + u_bandXSmooth, canvasUV.x);
  float rightEdge = 1.0 - smoothstep(u_bandX.y - u_bandXSmooth, u_bandX.y + u_bandXSmooth, canvasUV.x);
  float bandMask = leftEdge * rightEdge;

  // Parabolic waist gate, curved around the band's center.
  //   waistCurvature > 0 → arc dips lower at the center (∪-shaped).
  //   waistCurvature < 0 → arc bulges upward at the center (∩-shaped).
  float waistBase = mix(u_floor, u_ceiling, u_bandHeightRatio);
  float dx = canvasUV.x - (u_bandX.x + u_bandX.y) * 0.5;
  float waistY = waistBase + u_waistCurvature * dx * dx;
  float waistGate = smoothstep(waistY - u_waistSmooth, waistY + u_waistSmooth, canvasUV.y);

  // Floor gate: cut the band below the subject's feet so the effect never
  // spills past the object into empty banner space.
  float floorGate = 1.0 - smoothstep(u_floor, u_floor + u_waistSmooth, canvasUV.y);

  // mask: where the bg overlay should be visible (covers the subject below).
  float mask = bandMask * waistGate * floorGate;

  // Multiply only the alpha channel; RGB is the bg pixel as-is (non-premultiplied).
  fragColor = vec4(src.rgb, src.a * mask);
}
`;

/**
 * Parameters controlling the alpha falloff fragment shader.
 * Every spatial value is in canvas UV space [0, 1] with origin top-left.
 */
export type AlphaFalloffParams = {
  /** Horizontal band extent [x1, x2] in canvas UV */
  bandX: [number, number];
  /** Half-width of the band's lateral smooth edges */
  bandXSmooth: number;
  /** Subject's alpha top in canvas UV — the band's upper reference */
  ceiling: number;
  /** Subject's feet in canvas UV — the band's lower bound */
  floor: number;
  /** Waist height as a fraction of the subject's alpha height (0..1) */
  bandHeightRatio: number;
  /** Half-width of the smoothstep transition at the waist line */
  waistSmooth: number;
  /** Parabolic bend of the waist; 0 = flat, sign controls arc direction */
  waistCurvature: number;
};

/** Defaults tuned for half-body cutouts. */
export const DEFAULT_ALPHA_FALLOFF_PARAMS: AlphaFalloffParams = {
  bandX: [0.0, 1.0],
  bandXSmooth: 0.02,
  ceiling: 0.0,
  floor: 1.0,
  bandHeightRatio: 0.2,
  waistSmooth: 0.06,
  waistCurvature: -0.3
};
